import { z } from "zod";
import { EnergyReconciliation } from "./energy.js";
/**
 * One grid outage, rebuilt from the readings taken while the grid was missing
 * (docs/DECISIONS/0034).
 *
 * Readings come every five minutes or so, so neither edge is known exactly.
 * `start` is the first reading without the grid and `end` the first with it
 * back; the grid went somewhere after `lastWithGrid` and came back somewhere
 * after `lastWithoutGrid`. A client that states an edge should say so.
 *
 * Coverage is time, not energy: `coveredSeconds` is how long the readings
 * show the house powered without the grid. `unrecordedSeconds` is time nobody
 * saw, with the grid missing on both sides of it. `uncoveredSeconds` is time
 * the house had no power: readings showing the inverter's output off, and the
 * silence right before the grid came back when it outlasted the gap
 * threshold -- the inverter, which powers the dongle, most likely shut down
 * with an empty battery. The three add up to `durationSeconds`.
 */
export const Outage = z.object({
    start: z.string().datetime(),
    /** Null when records begin without the grid. */
    lastWithGrid: z.string().datetime().nullable(),
    /** Null while the outage goes on. */
    end: z.string().datetime().nullable(),
    lastWithoutGrid: z.string().datetime(),
    ongoing: z.boolean(),
    /** To `end`, or to the time of the request while it goes on. */
    durationSeconds: z.number().int(),
    coveredSeconds: z.number().int(),
    unrecordedSeconds: z.number().int(),
    uncoveredSeconds: z.number().int(),
    /** The part of `uncoveredSeconds` the readings show the inverter's output off. */
    outputOffSeconds: z.number().int(),
    /** `coveredSeconds` over `durationSeconds`, from 0 to 1. */
    coveredShare: z.number(),
    /** What the battery delivered, over the covered time only. */
    batteryKwh: z.number(),
    /** What the panels generated over the same time. */
    solarKwh: z.number(),
    /** What the house used over the same time. */
    loadKwh: z.number(),
    socStartPct: z.number(),
    socEndPct: z.number(),
    lowestSocPct: z.number(),
    /** The lowest charge was at or under the reserve floor as configured now. */
    reachedReserve: z.boolean(),
    /**
     * `batteryKwh` against the inverter's own discharge counter across the
     * outage. Null while it goes on, when any of it went unrecorded, or when the
     * counter is missing at either end.
     */
    dischargeCheck: EnergyReconciliation.nullable(),
});
export const OutagePage = z.object({
    timezone: z.string(),
    /** The reserve floor as configured now, to set `lowestSocPct` against. */
    reserveSocPct: z.number(),
    /** Readings further apart than this are a gap. */
    gapSeconds: z.number().int(),
    /** Newest first. */
    outages: z.array(Outage),
    /** Pass as `before` for the next, older page; null on the last. */
    nextBefore: z.string().datetime().nullable(),
});
/** Newest first, in pages, as every list that can grow must be (section 10). */
export const OutageQuery = z
    .object({
    limit: z.coerce.number().int().min(1).max(500).default(50),
    /** Outages that started before this instant. */
    before: z.string().datetime().optional(),
})
    .strict();
//# sourceMappingURL=outages.js.map